import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Box, Button } from "@mui/material";
import { $getNodeByKey } from "lexical";
import { useState } from "react";
import { X } from "../../../../styles/icons/x";
import { AddButtonListForm } from "../components/button/add-button-list-form";
import { DeleteButtonDialog } from "../components/button/delete-button-dialog";
import { EditorActionButtons } from "../components/button/editor-action-buttons";
import { EditorButton } from "../components/button/editor-button";
import { useLexicalContext } from "../lexical-context";
import { $isButtonListNode, ButtonListNode } from "./button-list-node";
import { convertButtonListElement, isButtonListElement } from "./utils";

import type { ButtonNodeBlock } from "../types/markdown-node-blocks";
import type { SerializedButtonListNode } from "./button-list-node";
import type { DOMConversionMap, NodeKey } from "lexical";
import type { FC } from "react";

const ButtonElement: FC<{ text: string; onClick?: () => void }> = ({ text, onClick }) => {
  const [hover, setHover] = useState(false);
  const { disabled } = useLexicalContext();

  return (
    <Box
      sx={{ display: "inline-block", position: "relative", pr: 1 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Button data-analytics-id="lexical-button-list-button-preview" sx={{ border: 1, p: "3px" }}>
        {text}
      </Button>
      {hover && !disabled && (
        <Box
          sx={{
            position: "absolute",
            right: -5,
            top: -12,
          }}
        >
          <EditorButton
            data-analytics-button="lexical-button-list-delete"
            onClick={onClick}
            icon={<X sx={{ height: 15, width: 15 }} color="primary" />}
            sx={{
              height: 15,
              width: 15,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

const EditorButtonList: FC<{
  nodeKey: NodeKey;
  buttons: ButtonNodeBlock[];
}> = ({ nodeKey, buttons }) => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [editor] = useLexicalComposerContext();
  const { filterTags } = useLexicalContext();

  const handleEdit = (data: { buttons: ButtonNodeBlock[] }): void => {
    if (data)
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (!$isButtonListNode(node)) return;
        else node.setButtons(data.buttons);
      });
    setOpenEditModal(false);
  };

  const handleDelete = (): void => {
    editor.update(() => {
      $getNodeByKey(nodeKey)?.remove();
    });
    setOpenDeleteModal(false);
  };

  return (
    <>
      <EditorActionButtons
        buttons={["edit"]}
        disabled={!editor.isEditable()}
        onEditClicked={() => setOpenEditModal(true)}
        onDeleteClicked={handleDelete}
        nodeKey={nodeKey}
        data-analytics-id="lexical-button-list"
      >
        {buttons.map((button, i) => (
          <ButtonElement
            key={i}
            text={button.text}
            onClick={() =>
              buttons.length === 1
                ? handleDelete()
                : handleEdit({ buttons: buttons.filter((x) => x.id !== button.id) })
            }
          />
        ))}
      </EditorActionButtons>
      <AddButtonListForm
        open={openEditModal}
        onCancel={() => setOpenEditModal(false)}
        filterTags={filterTags}
        disabled={false}
        initialState={{ buttons }}
        onConfirm={handleEdit}
      />
      <DeleteButtonDialog
        open={openDeleteModal}
        onCancel={() => {
          setOpenDeleteModal(false);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
};

export class ButtonListNodeMui extends ButtonListNode {
  decorate(): React.JSX.Element {
    return <EditorButtonList nodeKey={this.__key} buttons={this.__buttons} />;
  }
  static importJSON(serializedNode: SerializedButtonListNode): ButtonListNode {
    return super.importJSON(serializedNode);
  }
  exportJSON(): SerializedButtonListNode {
    return super.exportJSON();
  }
  static clone(node: ButtonListNode): ButtonListNode {
    return super.clone(node);
  }
  static getType(): string {
    return super.getType();
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (imageNode: HTMLElement) => {
        if (!isButtonListElement(imageNode)) return null;

        return {
          conversion: (n) => convertButtonListElement(n, ButtonListNodeMui),
          priority: 0,
        };
      },
    };
  }
}

export function $createButtonListNode(
  headless: boolean,
  buttons: ButtonNodeBlock[],
): ButtonListNode {
  return new (headless ? ButtonListNode : ButtonListNodeMui)(buttons);
}
